import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { API, useAuth } from "../App";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import {
  GraduationCap, PlayCircle, Lock, ChevronRight, Award, CheckCircle2,
  Download, Loader2,
} from "lucide-react";
import EmployeeCoursePlayer from "./EmployeeCoursePlayer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * EmployeeCourseTrainings — lists EAD courses where the employee already
 * signed the attendance list and shows the right CTA depending on
 * progress (video / quiz / certificate).
 *
 * Hidden when there's nothing to show.
 */
export default function EmployeeCourseTrainings({ refreshKey = 0 } = {}) {
  const { token } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openCourse, setOpenCourse] = useState(null);

  const load = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/employee/course-trainings`, {
        params: { token },
      });
      setItems(res.data?.items || []);
    } catch (_e) { /* silent */ }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load, refreshKey]);

  if (loading) return null;
  // Only show courses that still require action from the employee.
  // Once approved, the certificate already lives in "Meus Cursos",
  // so there's no need to keep a redundant card in the dashboard.
  const visible = items.filter((it) =>
    it.ui_status === "video_pending" || it.ui_status === "quiz_pending"
  );
  if (!visible.length) return null;

  return (
    <>
      <div className="space-y-2" data-testid="course-trainings-list">
        {visible.map((it) => (
          <CourseRow
            key={`${it.course_key}-${it.list_id}`}
            item={it}
            onOpen={() => setOpenCourse(it.course_key)}
            token={token}
          />
        ))}
      </div>

      <EmployeeCoursePlayer
        open={!!openCourse}
        courseKey={openCourse}
        onClose={() => { setOpenCourse(null); load(); }}
      />
    </>
  );
}

function CourseRow({ item, onOpen, token }) {
  const cfg = uiCfg(item.ui_status);
  return (
    <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}>
      <Card className={`border ${cfg.border} bg-gradient-to-r ${cfg.bg}`}>
        <CardContent className="p-3.5 sm:p-4 flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex-1 flex items-start gap-3">
            <div className={`rounded-lg p-2 mt-0.5 ${cfg.iconBg}`}>
              {cfg.icon}
            </div>
            <div className="min-w-0">
              <div className={`text-[10px] uppercase font-semibold tracking-wider ${cfg.tag}`}>
                {cfg.title}
              </div>
              <div className="text-sm font-semibold text-slate-900">
                {item.course_title}
              </div>
              <div className="text-xs text-slate-600 mt-0.5 flex items-center gap-2 flex-wrap">
                <span>Carga horária: <strong>{item.carga_horaria}</strong></span>
                {item.video_completed_at && (
                  <Badge variant="outline" className="text-[10px] py-0 px-1.5 border-emerald-300 text-emerald-700">
                    Vídeo concluído
                  </Badge>
                )}
                {item.best_score != null && (
                  <Badge variant="outline" className="text-[10px] py-0 px-1.5">
                    Melhor nota: {item.best_score}%
                  </Badge>
                )}
              </div>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
            {item.ui_status === "approved" && (
              <Button
                size="sm"
                className="bg-amber-600 hover:bg-amber-700 text-white"
                onClick={() => downloadCert(item.course_key, token)}
                data-testid={`course-trainings-cert-${item.course_key}`}
              >
                <Download className="w-3.5 h-3.5 mr-1" />
                Certificado
              </Button>
            )}
            <Button
              size="sm"
              className={cfg.btnClass}
              onClick={onOpen}
              data-testid={`course-trainings-open-${item.course_key}`}
            >
              {cfg.cta}
              <ChevronRight className="w-3.5 h-3.5 ml-1" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}

function downloadCert(courseKey, token) {
  const url = `${BACKEND_URL}/api/employee/courses/${courseKey}/certificate?token=${encodeURIComponent(token)}`;
  window.open(url, "_blank");
}

function uiCfg(status) {
  switch (status) {
    case "video_pending":
      return {
        title: "Curso EAD — Assistir aula",
        icon: <PlayCircle className="w-5 h-5 text-indigo-700" />,
        iconBg: "bg-indigo-600/10",
        tag: "text-indigo-700",
        bg: "from-indigo-50 to-white",
        border: "border-indigo-300",
        cta: "Iniciar aula",
        btnClass: "bg-indigo-600 hover:bg-indigo-700 text-white",
      };
    case "quiz_pending":
      return {
        title: "Curso EAD — Avaliação pendente",
        icon: <Lock className="w-5 h-5 text-emerald-700" />,
        iconBg: "bg-emerald-600/10",
        tag: "text-emerald-700",
        bg: "from-emerald-50 to-white",
        border: "border-emerald-300",
        cta: "Fazer avaliação",
        btnClass: "bg-emerald-600 hover:bg-emerald-700 text-white",
      };
    case "approved":
      return {
        title: "Curso EAD — Concluído",
        icon: <Award className="w-5 h-5 text-amber-700" />,
        iconBg: "bg-amber-600/10",
        tag: "text-amber-700",
        bg: "from-amber-50 to-white",
        border: "border-amber-300",
        cta: "Rever",
        btnClass: "bg-slate-700 hover:bg-slate-800 text-white",
      };
    default:
      return {
        title: "Curso EAD",
        icon: <GraduationCap className="w-5 h-5 text-slate-600" />,
        iconBg: "bg-slate-200",
        tag: "text-slate-700",
        bg: "from-slate-50 to-white",
        border: "border-slate-300",
        cta: "Abrir",
        btnClass: "bg-slate-700 hover:bg-slate-800 text-white",
      };
  }
}
